import { useEffect, useRef } from "react";
import { ApiError, apiFetch } from "@contractor-quoting/api-client";

const reconciliationDelaysMs = [2_000, 3_500, 5_000, 8_000, 12_000, 15_000] as const;

interface PendingChat {
  pending: boolean;
}

interface PendingChatReconciliationOptions<T extends PendingChat> {
  enabled: boolean;
  endpoint: string;
  onResult: (chat: T) => void;
  onAuthenticationError?: (error: ApiError) => void;
}

/**
 * Reconcile pending chat work with bounded backoff.
 *
 * Each response schedules the next request only while work remains pending,
 * preventing fixed-interval requests from overlapping or polling through long
 * hosted-runtime cold starts.
 */
export function usePendingChatReconciliation<T extends PendingChat>({
  enabled,
  endpoint,
  onResult,
  onAuthenticationError,
}: PendingChatReconciliationOptions<T>): void {
  const onResultRef = useRef(onResult);
  const onAuthenticationErrorRef = useRef(onAuthenticationError);
  onResultRef.current = onResult;
  onAuthenticationErrorRef.current = onAuthenticationError;

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let timer: number | undefined;
    let attempt = 0;

    const schedule = () => {
      timer = window.setTimeout(() => void reconcile(), reconciliationDelay(attempt));
    };
    const reconcile = async () => {
      try {
        const chat = await apiFetch<T>(endpoint, { method: "POST" });
        if (cancelled) return;
        onResultRef.current(chat);
        if (!chat.pending) return;
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ApiError && [401, 403].includes(error.status)) {
          onAuthenticationErrorRef.current?.(error);
          return;
        }
      }
      attempt += 1;
      schedule();
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [enabled, endpoint]);
}

export function reconciliationDelay(attempt: number): number {
  return reconciliationDelaysMs[Math.min(attempt, reconciliationDelaysMs.length - 1)]!;
}
